import * as React from "react";
import { format, isMatch } from "date-fns";
import { Badge, Button, Tooltip } from "@jhool-io/fe-components";
import {
    ClaimActivityType,
    ClaimStatus,
    ClaimTag,
    IClaim,
    IClaimActivity,
} from "../types/claims.types";
import {
    displayNameInRightFormat,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../utils/helpers";
import UserIcon from "../../../icons/User";
import InfoIcon from "../../../icons/Info";

const claimStatusDescriptions: Record<ClaimStatus, string> = {
    [ClaimStatus.ACKNOWLEDGED]: "",
    [ClaimStatus.APPEALED]:
        "The claim has been appealed with the insurance company",
    [ClaimStatus.CLOSED]: "",
    [ClaimStatus.DENIED]: "",
    [ClaimStatus.ON_HOLD]: "",
    [ClaimStatus.PAID]:
        "Remit has been paid, but the biller still needs to review it before closing it",
    [ClaimStatus.PATIENT_PAYMENT_COLLECTION]:
        "Needs to be paid by the patient (PR100)",
    [ClaimStatus.PENDING]: "Received by Waystar and being processed",
    [ClaimStatus.PENDING_RECOUPMENT]:
        "Waiting for the insurance company to recoup payment for this claim",
    [ClaimStatus.READY_FOR_SUBMISSION]: "",
    [ClaimStatus.RECEIVED]: "",
    [ClaimStatus.REJECTED]: "Rejected by Waystar or the payer",
    [ClaimStatus.REVERSED]: "",
    [ClaimStatus.SUBMITTED]: "Sent to Waystar",
};

export const getStatusTag = (status: ClaimStatus, text?: string) => {
    let bgColor: string;
    let textColor: string;

    switch (status) {
        case ClaimStatus.ACKNOWLEDGED:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case ClaimStatus.DENIED:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;
        case ClaimStatus.PAID:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case ClaimStatus.PENDING:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        case ClaimStatus.RECEIVED:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case ClaimStatus.PATIENT_PAYMENT_COLLECTION:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case ClaimStatus.CLOSED:
            textColor = "var(--color-neutral-text)";
            bgColor = "var(--color-neutral-bg)";
            break;
        case ClaimStatus.SUBMITTED:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case ClaimStatus.APPEALED:
            textColor = "var(--color-info-text)";
            bgColor = "var(--color-info-bg)";
            break;
        case ClaimStatus.REJECTED:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;
        case ClaimStatus.READY_FOR_SUBMISSION:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        case ClaimStatus.REVERSED:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;

        case ClaimStatus.PENDING_RECOUPMENT:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;

        default:
            textColor = "var(--color-neutral-text)";
            bgColor = "var(--color-neutral-bg)";
    }

    return (
        <Badge
            content={`${text ? `${text} ` : ``}${removeEnumUnderscore(status)}`}
            textColor={textColor}
            bgColor={bgColor}
            type="custom"
        />
    );
};

export const getClaimStatusFilterLabel = (status: ClaimStatus) => {
    const description = claimStatusDescriptions[status];
    const label = removeEnumUnderscore(status);

    return description ? (
        <Tooltip content={description} classNames="!z-[100]">
            <span className="flex items-center gap-x-2">
                {label} <InfoIcon className="size-3" />
            </span>
        </Tooltip>
    ) : (
        <span>{label}</span>
    );
};

export const calculateClaimAge = (time: string) => {
    const dateSubmitted = new Date(time);
    const currentTime = new Date();
    const timeDifference = currentTime.getTime() - dateSubmitted.getTime();

    // Convert milliseconds to seconds
    const secondsDifference = Math.floor(timeDifference / 1000);

    if (secondsDifference < 60) {
        return `${secondsDifference} seconds`;
    }
    if (secondsDifference < 3600) {
        const minutes = Math.floor(secondsDifference / 60);
        return `${minutes} minute${minutes > 1 ? "s" : ""}`;
    }
    if (secondsDifference < 86400) {
        const hours = Math.floor(secondsDifference / 3600);
        return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
    const days = Math.floor(secondsDifference / 86400);
    return `${days} day${days > 1 ? "s" : ""}`;
};

/**
 * Handles the extraction of the separate parts of a phone number, i.e country code, area code & number. Returns an array of all three if passed a valid number string.
 * @param phoneNumberString
 * @returns [countryCode, areaCode, phoneNumber]
 */
export const getPhoneNumberParts = (phoneNumberString: string) => {
    const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return [match[1], match[2], `${match[3]}-${match[4]}`];
    }
    return [];
};

export const getClaimsListNavigateUrl = () => {
    const claimsListFiltersFromLS = localStorage.getItem("claimslist_filters");

    if (claimsListFiltersFromLS) {
        return `/claims?${claimsListFiltersFromLS}`;
    }
    return `/claims`;
};

// function to return styles for claim dropdown based on status
export const getClaimStatusTagStylesForDropdown = (status: ClaimStatus) => {
    let className: string;
    let strokeColor: string;

    switch (status) {
        case ClaimStatus.ACKNOWLEDGED:
            className = "text-[#00563E] bg-[#ccfae97f] stroke-[#00563E]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.DENIED:
            className = "text-[#981F41] bg-[#fbc7c67f] stroke-[#981F41]";
            strokeColor = "#981F41";
            break;
        case ClaimStatus.PAID:
            className = "text-[#00563E] bg-[#ccfae97f] stroke-[#00563E]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.PENDING:
            className = "text-[#634D17] bg-[#f7e5a47f] stroke-[#634D17]";
            strokeColor = "#634D17";
            break;
        case ClaimStatus.RECEIVED:
            className = "text-[#00563E] bg-[#ccfae97f] stroke-[#00563E]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.PATIENT_PAYMENT_COLLECTION:
            className = "text-[#00563E] bg-[#ccfae97f] stroke-[#00563E]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.CLOSED:
            className = "text-[#212121] bg-[rgb(235,235,235)] stroke-[#0B132B]";
            strokeColor = "#0B132B";
            break;
        case ClaimStatus.SUBMITTED:
            className = "text-[#00563E] bg-[#ccfae97f] stroke-[#00563E]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.APPEALED:
            className = "text-[#165574] bg-[#acdefa7f] stroke-[#165574]";
            strokeColor = "#165574";
            break;
        case ClaimStatus.REJECTED:
            className = "text-[#981F41] bg-[#fbc7c67f] stroke-[#981F41]";
            strokeColor = "#981F41";
            break;
        case ClaimStatus.READY_FOR_SUBMISSION:
            className = "text-[#634D17] bg-[#f7e5a47f] stroke-[#634D17]";
            strokeColor = "#634D17";
            break;
        case ClaimStatus.REVERSED:
            className = "text-[#981F41] bg-[#fbc7c67f] stroke-[#981F41]";
            strokeColor = "#981F41";
            break;
        case ClaimStatus.PENDING_RECOUPMENT:
            className = "text-[#634D17] bg-[#f7e5a47f] stroke-[#634D17]";
            strokeColor = "#634D17";
            break;
        default:
            className = "text-[#212121] bg-[rgb(235,235,235)] stroke-[#212121]";
            strokeColor = "#212121";
    }

    return {
        className,
        strokeColor,
    };
};

export const getClaimTagBadge = (claimTag?: ClaimTag) => {
    if (claimTag !== ClaimTag.OLD) return null;

    return <Badge content="Old" type="info" />;
};

export const getClaimActivityToDisplay = (
    claimActivities: IClaimActivity[] | null,
    activityType: ClaimActivityType,
    userId: string
) => {
    let claimActivitiesToCheck: IClaimActivity[] | null = null;
    let claimActivityForUser: IClaimActivity | null = null;

    if (!claimActivities || (claimActivities && claimActivities.length === 0)) {
        claimActivitiesToCheck = null;
    }

    claimActivitiesToCheck =
        claimActivities?.filter(
            (claimAct) => claimAct.activity_type === activityType
        ) ?? null;

    claimActivityForUser =
        claimActivitiesToCheck?.find(
            (claimAct) =>
                claimAct?.user?.id === userId &&
                claimAct.activity_type === activityType
        ) ?? null;

    const isClaimAssignedToMe = Boolean(claimActivityForUser);

    const usersClaimActivityIsRelatedToFunction = () => {
        if (claimActivitiesToCheck && claimActivitiesToCheck.length > 0) {
            if (claimActivitiesToCheck.length > 1) {
                return (
                    <Tooltip
                        content={`Assigned to: ${
                            claimActivityForUser ? "You," : ""
                        } ${claimActivitiesToCheck
                            .filter((claimAct) => claimAct.user.id !== userId)
                            .map(
                                (claimAct) =>
                                    `${displayNameInRightFormat(
                                        {
                                            firstName: claimAct.user.first_name,
                                            lastName: claimAct.user.last_name,
                                        },
                                        false
                                    )}`
                            )
                            .join(" , ")}`}
                    >
                        <Button size="auto" variant="normal">
                            <Badge
                                bgColor="var(--color-info-bg)"
                                textColor="var(--color-info-text)"
                                type="custom"
                                content={
                                    <span className="flex items-center gap-x-1 text-[12px]">
                                        <UserIcon className="size-[12px] stroke-[--color-info-text]" />
                                        {`${
                                            isClaimAssignedToMe
                                                ? `You and `
                                                : ""
                                        } ${
                                            isClaimAssignedToMe
                                                ? claimActivitiesToCheck.length -
                                                  1
                                                : claimActivitiesToCheck.length
                                        } user${
                                            isClaimAssignedToMe &&
                                            claimActivitiesToCheck.length -
                                                1 ===
                                                1
                                                ? ""
                                                : "s"
                                        }`}
                                    </span>
                                }
                            />
                        </Button>
                    </Tooltip>
                );
            }

            if (claimActivitiesToCheck.length === 1 && !isClaimAssignedToMe) {
                return (
                    <Badge
                        bgColor="var(--color-info-bg)"
                        textColor="var(--color-info-text)"
                        type="custom"
                        content={
                            <span className="flex items-center gap-x-1">
                                <UserIcon className="size-[12px] stroke-[--color-info-text]" />
                                {displayNameInRightFormat(
                                    {
                                        firstName:
                                            claimActivitiesToCheck[0].user
                                                .first_name,
                                        lastName:
                                            claimActivitiesToCheck[0].user
                                                .last_name,
                                    },
                                    false
                                )}
                            </span>
                        }
                    />
                );
            }

            if (claimActivitiesToCheck.length === 1 && isClaimAssignedToMe) {
                return (
                    <Badge
                        bgColor="var(--color-info-bg)"
                        textColor="var(--color-info-text)"
                        type="custom"
                        content={
                            <span className="flex items-center gap-x-1 text-[12px]">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="var(--color-info-text)"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-[12px] stroke-[--color-info-text]"
                                >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                                You
                            </span>
                        }
                    />
                );
            }
        }

        return "--";
    };

    return {
        claimActivityForUser,
        isClaimAssignedToMe,
        usersNameToDisplay: usersClaimActivityIsRelatedToFunction(),
    };
};

export function getClaimCptCodeEntriesFromServiceDetails(
    serviceDetails: unknown
): string[] {
    if (!Array.isArray(serviceDetails)) {
        return [];
    }

    return serviceDetails.flatMap((service) => {
        if (!service || typeof service !== "object") {
            return [];
        }
        const claimService = service as {
            procedureCode?: string;
            units?: number | string;
        };
        const cptCode = claimService.procedureCode?.trim();
        if (!cptCode) {
            return [];
        }
        const units = Number(claimService.units ?? 1);
        const unitCount = Number.isNaN(units) ? 1 : Math.max(1, units);
        return Array.from({ length: unitCount }, () => cptCode);
    });
}

export function getClaimCptCodeEntries(claim: IClaim): string[] {
    if (Array.isArray(claim.claim_contents) && claim.claim_contents.length) {
        const cptCodesFromParentClaim = claim.claim_contents.flatMap(
            (claimContent) => {
                if (!claimContent || typeof claimContent !== "object") {
                    return [];
                }
                const content = claimContent as {
                    serviceDetails?: unknown;
                };
                return getClaimCptCodeEntriesFromServiceDetails(
                    content.serviceDetails
                );
            }
        );
        if (cptCodesFromParentClaim.length > 0) {
            return cptCodesFromParentClaim;
        }
    }

    const claimContent = claim.claim_content as
        | { serviceDetails?: unknown }
        | undefined;
    const cptCodesFromClaim = getClaimCptCodeEntriesFromServiceDetails(
        claimContent?.serviceDetails
    );
    if (cptCodesFromClaim.length > 0) {
        return cptCodesFromClaim;
    }

    if (Array.isArray(claim.cpt_codes) && claim.cpt_codes.length > 0) {
        return claim.cpt_codes.filter(Boolean);
    }
    return claim.cpt_code ? [claim.cpt_code] : [];
}

export function getClaimCptCodesDisplay(claim: IClaim): React.ReactNode {
    const cptCodes = getClaimCptCodeEntries(claim);
    if (cptCodes.length === 0) {
        return "--";
    }

    const cptCodeCountMap = cptCodes.reduce((map, cptCode) => {
        map.set(cptCode, (map.get(cptCode) ?? 0) + 1);
        return map;
    }, new Map<string, number>());

    return (
        <span className="flex flex-wrap items-center gap-2">
            {Array.from(cptCodeCountMap.entries()).map(([cptCode, count]) => (
                <span key={cptCode} className="inline-flex items-center gap-1">
                    <span>{cptCode}</span>
                    {count > 1 ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                            x {count}
                        </span>
                    ) : null}
                </span>
            ))}
        </span>
    );
}

export const formatClaimStatus = (status: ClaimStatus) =>
    makeStringFirstLetterCapital(removeEnumUnderscore(status));

export const formatClaimServiceDate = (date: string) => {
    if (isMatch(date, "yyyy-MM-dd")) {
        return format(new Date(`${date}T00:00:00`), "MM/dd/yyyy");
    }
    return date;
};
